<template>
  <pl-dialog title="New RoundTable" size="relative_sm" @close="handleCloseDialog()" :disableOutsideClick="true">
    <div class="flex flex-col pb-12 pt-1 items-center px-6 flex-grow h-full">
      <div class="flex items-center space-x-2.5 mb-6">
        <div class="flex items-center border border-solid border-gray-200 rounded-full relative px-1 py-1 h-10">
          <button @click="handleChangeRoundTableType('written')" class="flex items-center space-x-2 rounded-full h-full px-4 font-manrope" :class="{'bg-blue text-white': roundtableType === 'written'}">
            <img :src="roundtableType === 'written' ? '/static/roundtable_icons/written_icon.svg' : '/static/roundtable_icons/written_icon_black.svg'" class="w-3.5 h-3.5"/>
            <span>Written</span>
          </button>
          <button @click="handleChangeRoundTableType('verbal')" class="flex items-center space-x-2 rounded-full h-full px-4 font-manrope" :class="{'bg-green-light text-white': roundtableType === 'verbal'}">
            <img :src="roundtableType === 'verbal' ? '/static/roundtable_icons/verbal_icon.svg' : '/static/roundtable_icons/verbal_icon_black.svg' " class="w-4 h-4"/>
            <span>Verbal</span>
          </button>
        </div>
      </div>
      <div :class="['flex flex-col space-y-2.5 rounded-xl pl-1.5 pr-1 pt-1.5 pb-2 mb-5 w-full bg-gray-100 max-w-2xl relative', roundtableInputType === 'youtube' ? 'ring-2 ring-offset-2 ring-primary' : 'focus-within:ring-2 ring-offset-2 ring-primary']">
        <div class="flex justify-between items-center relative w-full">
          <div class="flex items-center w-full">
            <div v-if="roundtableInputType && roundtableInput" class="ml-2 mr-1">
              <div v-if="roundtableInputType !== ''">
                <i v-if="roundtableInputType === 'youtube'" class="fa-brands fa-youtube text-red-600"></i>
                <i v-if="roundtableInputType === 'article'" class="fa-solid fa-link"></i>
                <i v-if="roundtableInputType === 'topic'" class="fa-solid fa-quote-left"></i>
                <i v-if="roundtableInputType === 'pdf'" class="fa-regular fa-file-lines"></i>
              </div>
            </div>
            <div v-if="!documentUpload && !isUploading" class="w-full">
              <input
                ref="inputRef"
                v-model="roundtableInput"
                @keyup.enter="handleOpenDraft()"
                :disabled="isGeneratingPromt"
                class="border-none w-11/12 pl-2 py-1 bg-gray-100 disabled:opacity-50 font-manrope"
                placeholder="Enter a topic, paste any URL, or upload a PDF..."
              />
            </div>
            <div v-else-if="isUploading" class="flex items-center space-x-2 pl-2 py-1">
              <div class="flex items-center space-x-2 justify-center bg-gray-100">
                <div class="loader-primary"></div>
              </div>
              <span class="font-manrope text-xs text-gray-800">Uploading...</span>
            </div>
            <div v-else class="flex items-center space-x-2" @mouseover="isMouseHoverUrl = true" @mouseleave="isMouseHoverUrl = false">
              <div class="border-none w-11/12 pl-2 py-1 bg-gray-100 font-manrope">
                <a class="underline text-blue-500 hover:text-blue-600" :href="documentUpload.aws_url" target="_blank">
                  {{ documentUpload.name }}
                </a>
              </div>
              <button v-show="isMouseHoverUrl === true" @click="handleRemoveFile()" class="w-5 h-5 hover:bg-red-200 bg-red-100 text-red-500 rounded-full" style="font-size: 10px;">
                <i class="fa-solid fa-minus"></i>
              </button>
            </div>
          </div>
          <div class="absolute flex items-center gap-x-2 right-0.5 top-1/2 transform -translate-y-1/2">
            <input
              ref="fileInput"
              type="file"
              accept=".pdf"
              class="hidden"
              @change="onFileChange"
            />
            <button @click.stop="handleTriggerUploadPDF()" class="flex items-center justify-center text-gray-800 w-7 h-7 hover:bg-gray-200 rounded-lg transition-all duration-100 cursor-pointer">
              <i  class="fa-solid fa-paperclip"></i>
            </button>
            <button @click="handleOpenDraft()" class="text-white w-7 h-7 rounded-lg bg-primary hover:bg-primary-dark flex justify-center items-center cursor-pointer">
              <i v-if="isGeneratingPromt === false" class="fa-solid fa-arrow-right-long"></i>
              <div v-if="isGeneratingPromt === true" class="loader"></div>
            </button>
          </div>
        </div>
        <ul class="flex items-center space-x-1 ml-0.5 text-xs font-manrope text-gray-800">
          <li 
            v-for="(level, index) in gradeLevels"
            @click.stop="handleSelectGradeLevel(level)"
            :key="level"
            :class="[
              'px-2 py-0.5 rounded-md bg-white cursor-pointer border border-solid hover:border-primary',
              gradeLevel === level ? 'border-primary' : 'border-white'
            ]"
          >
            <span>{{ level }}</span>
          </li>
        </ul>
      </div>
      <div class="flex items-center space-x-2 text-center">
        <button @click="handleOpenDraft()" class="flex items-center space-x-3 hover:bg-gray-50 border border-solid border-gray-200 rounded-full px-4 py-2  text-center font-manrope text-gray-700 hover:text-gray-900 font-light">
          <img src="/static/logos/new_roundtable_icon.svg" class="w-3.5 h-3.5"/>
          <span>Start from scratch</span>
        </button>
        <button @click="handleGoToUniverse()" class="flex items-center space-x-3 hover:bg-gray-50 border border-solid border-gray-200 rounded-full px-4 py-2  text-center font-manrope text-gray-700 hover:text-gray-900 font-light">
          <img src="/static/roundtable_options_create/browse_universe.svg" class="w-3.5 h-3.5"/>
          <span>Browse Universe</span>
        </button>
        <button @click="handleGoToPortfolio()" class="flex items-center space-x-2.5 hover:bg-gray-50 border border-solid border-gray-200 rounded-full px-4 py-2 text-center font-manrope text-gray-700 hover:text-gray-900 font-light">
          <img src="/static/roundtable_icons/folder.svg" class="w-4 h-4"/>
          <span>My Portfolio</span>
        </button>
        <button v-if="is_teacher && free_teacher_account" @click="handleUpgradeAccount()" class="flex items-center space-x-2 hover:bg-gray-50 border border-solid border-gray-200 rounded-full px-4 py-2 text-center font-manrope text-gray-700 hover:text-gray-900 font-light">
          <i class="fas fa-rocket text-pinky"></i>
          <span>{{ getProfile.activity_passes }}</span>
        </button>
      </div>
    </div>
  </pl-dialog>
</template>
<script>
  import { ParlayWrittenTemplate } from '../../../features/dashboard/NewRoundTable/Templates/WrittenTemplate';
  import Checkbox from '../New_RoundTable/Components/Checkbox.vue';
  import Axios from 'axios';
  import { mapGetters } from 'vuex';
  import URLconfigs from 'services/apiConfigs';
  import authenticationServices from 'features/authentication/authentication.services';
  import profileServices from 'services/profile/profileServices';
  import { EventBus } from '../../../utils/event-bus';
  import amazonServices from 'services/amazonAWS';
  export default {
    name: 'GenerateRoundTable',
    components: {
      Checkbox,
    },
    data() {
      return {
        roundtableType: 'written',
        roundtableInputType: '',
        isUploading: false,
        roundtableInput: '',
        youtubeData: {},
        isGettingYoutubeInfo: false,
        isGeneratingPromt: false,
        gradeLevel: null,
        documentUpload: null,
        gradeLevels: ['5-6', '7-8', '9-10', '11-12', 'Higher'],
        isMouseHoverUrl: false,
      };
    },
    watch: {
      'roundtableInput': async function(newValue) {
        if (!newValue || newValue.trim() === '') {
          this.roundtableInputType = '';
          return;
        }

        const urlRegex = /(https?:\/\/[^\s]+)/gi;
        const matches = newValue.match(urlRegex);

        if (!matches) {
          this.roundtableInputType = 'topic';
          return;
        }

        const foundYoutubeUrl = matches.find(match => {
          try {
            const urlObj = new URL(match);
            const hostname = urlObj.hostname.toLowerCase();
            return hostname.includes('youtube.com') || hostname.includes('youtu.be');
          } catch (error) {
            return false;
          }
        });

        if (foundYoutubeUrl) {
          this.roundtableInputType = 'youtube';

        } else {
          this.roundtableInputType = 'article';
        }
      }
    },
    computed: {
      ...mapGetters([
        'hasTeacherPermissionsInCourse',
        'getProfile',
        'getCourseDetail',
        'getDashboardRole',
      ]),
      is_teacher() {
        return this.getProfile.role === 'teacher';
      },
      free_teacher_account() {
        return !this.getProfile.is_paid;
      },
    },
    methods: {
      handleRemoveFile: async function() {
        this.roundtableInputType = '';
        this.roundtableInput = '';
        this.documentUpload = null;
        this.isUploading = false;
      },
      onFileChange(event) {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
          alert('Only PDF files...');
          event.target.value = '';
          return;
        }
        const formattedFile = new File([file], file.name, { type: 'application/pdf' });
        this.handleUploadPDF(formattedFile);
        event.target.value = '';
      },
      handleTriggerUploadPDF: function() {
        this.$refs.fileInput.click();
      },
      handleUploadPDF: async function(file) {
        try {
          this.isUploading = true;
          const response = await amazonServices.postFileToS3(file, 'genie');
          if (response) {
            this.roundtableInputType = 'pdf';
          }
          this.documentUpload = response.data;
          this.roundtableInput = response.data.aws_url ? response.data.aws_url : null,
          this.isUploading = false;
        } catch (error) {
          console.error(error);
          this.isUploading = false;
        }
      },
      handleCloseDialog: function() {
        this.$emit('close');
      },
      handleUpgradeAccount: function() {
        this.$router.push({ name: 'profile.membership' });
      },
      handleChangeRoundTableType: function(type) {
        this.roundtableType = type;
      },
      handleSelectGradeLevel: function(level) {
        if (level === this.gradeLevel) {
          this.gradeLevel = '';
          return;
        }
        this.gradeLevel = level;
      },
      handleOpenDraft: async function() {
        try {
          this.isGeneratingPromt = true;
          const payload = {
            title: "",
            prompt: ParlayWrittenTemplate,
            type: this.roundtableType,
            instructions: "Review the resources and the questions before the Verbal RoundTables starts. Take notes about answers that you have to the questions, and use your notepad to keep track of your ideas as the conversation unfolds.",
            user_id: this.getProfile.id,
            courses: [this.getCourseDetail.id]
          };
          if (!this.getProfile.is_paid && this.getProfile.activity_passes === 0) {
            const toast = { type: 'Info', message: 'Sorry, you have no free RoundTables left! Delete an old RoundTable or upgrade your account!', time: 5000, upgrade: true };
            return EventBus.$emit('createToast', toast);
          }
          const draft = await Axios.post(`/api/drafts/draft`, payload);
          if (!draft) {
            throw new Error('Something happened starting roundtable creation! Please try again');
          }
          await this.$store.dispatch('useActivityPass');
          this.handleCloseDialog();
          this.$emit('handleStartDraft', {draft: draft.data, input: this.roundtableInput, gradeLevel: this.gradeLevel });
          this.isGeneratingPromt = false;
        } catch (error) {
          console.error(error);
        }
      },
      handleGoToUniverse: function() {
        const universe_url = `${URLconfigs.universeURL}/roundtables/browse`;
        authenticationServices.navigateToExternalWithAuth(universe_url);
      },
      handleGoToPortfolio: function() {
        if (!this.getProfile.profile_url) {
          profileServices.createProfile(this.getProfile.id).then(({ data }) => {
            const profileUrl = `${URLconfigs.universeURL}/profiles/${data.profile_url}`;
            authenticationServices.navigateToExternalWithAuth(profileUrl);
          }).catch((error) => {
            console.error(error);
          });
        } else {
          const profileUrl = `${URLconfigs.universeURL}/profiles/${this.getProfile.profile_url}`;
          authenticationServices.navigateToExternalWithAuth(profileUrl);
        }
      },
      
    },
    mounted: async function () {},
  };
</script>
<style scoped>
  .unopen {
    box-shadow: 3px 0px 0px #eb2d7c inset;
  }
  .loader,
  .loader:after {
    border-radius: 50%;
    width: 16px;
    height: 16px;
    margin-left: -0.6px;
    margin-top: -1px;
  }
  .loader {
    border-top: 3px solid rgba(255, 255, 255, 0.2);
    border-right: 3px solid rgba(255, 255, 255, 0.2);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
</style>



// WEBPACK FOOTER //
// GenerateRoundTable.vue?188bbfb0